import type {ColumnSizingInfoState, OnChangeFn} from '@tanstack/react-table'
import {useCallback, useState} from 'react'

import {MIN_CELL_WIDTH} from '../../../components/react_table/constants'
import {parseColumnId} from '../../../helpers/parsing'
import {useColumnWidth} from '../../../state-providers/columns/use-column-width'

const initialColumnSizingInfoState: ColumnSizingInfoState = {
  isResizingColumn: false,
  startOffset: null,
  startSize: null,
  deltaOffset: null,
  deltaPercentage: null,
  columnSizingStart: [],
}

export const useColumnSizingInfo = () => {
  const {updateWidth} = useColumnWidth()
  const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>(initialColumnSizingInfoState)

  const onColumnSizingInfoChange: OnChangeFn<ColumnSizingInfoState> = useCallback(
    updater => {
      const newState = typeof updater === 'function' ? updater(columnSizingInfo) : updater
      const previousResizingColumnId = columnSizingInfo.isResizingColumn

      // Listen for when we stop resizing a column (i.e., isResizingColumn becomes false)
      if (previousResizingColumnId && !newState.isResizingColumn && columnSizingInfo.startSize !== null) {
        const columnModelId = parseColumnId(previousResizingColumnId)
        // Persist the final size for the column, which must never be less than MIN_CELL_WIDTH
        const finalSize = Math.max(columnSizingInfo.startSize + (columnSizingInfo.deltaOffset ?? 0), MIN_CELL_WIDTH)

        if (columnModelId) updateWidth(columnModelId, finalSize)
      }

      setColumnSizingInfo(newState)
    },
    [columnSizingInfo, updateWidth],
  )

  return {columnSizingInfo, onColumnSizingInfoChange}
}
